import { useQueries } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import { handleZodError, useAxiosInstance } from './utils';

const URL = '/group/overview';

const TenantOverviewSchema = z.object({
  subdomain: z.string(),
  invoicesToTreat: z.number(),
  debtorsToHandle: z.number(),
  ongoingTasksCount: z.number(),
  paymentCount: z.number(),
  unprocessedEmailsCount: z.number(),
  unreadThirdPartyMessagesCount: z.number(),
  failedRemindersCount: z.number(),
  latePaymentPlansCount: z.number(),
});

const TenantOverviewResponseSchema = z.object({ tenant: TenantOverviewSchema });

export type GroupOverview = { tenants: z.infer<typeof TenantOverviewSchema>[] };

export async function loadTenantOverviewQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  subdomain: string,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { headers: { tenant: subdomain } });

  const parseResult = TenantOverviewResponseSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'groupOverview');
    throw new SilentError();
  }

  return parseResult.data.tenant;
}

export function useLoadGroupOverview({
  subdomains,
  isEnabled,
}: {
  subdomains: string[];
  isEnabled: boolean;
}) {
  const axiosInstance = useAxiosInstance();

  return useQueries({
    queries: subdomains.map(subdomain => ({
      queryKey: ['group-overview', subdomain],
      queryFn: () => loadTenantOverviewQueryFn(axiosInstance, subdomain),
      enabled: isEnabled,
    })),
    combine: results => {
      const isGroupOverviewLoading = results.some(({ isPending }) => isPending);
      const tenants = results.flatMap(({ data }) => (data ? [data] : []));

      return {
        groupOverview: isGroupOverviewLoading ? undefined : { tenants },
        failedSubdomains: results.flatMap(({ isError }, index) =>
          isError ? [subdomains[index]] : [],
        ),
        isGroupOverviewLoading,
      };
    },
  });
}
